/**
 * billingCalculation.js
 * 
 * Accurate billing unit calculation for Georgia DBHDD Medicaid services.
 * 
 * KEY CONCEPTS:
 * - Most DBHDD services bill in 15-minute units (time-based)
 * - The "8-minute rule" (CMS) applies: you need at least 8 minutes of a unit to bill it
 *   - 0–7 min = 0 units (not billable)
 *   - 8–22 min = 1 unit
 *   - 23–37 min = 2 units
 *   - 38–52 min = 3 units
 *   - 53–67 min = 4 units
 *   - etc.
 * - Some services are per-encounter (flat) rather than time-based
 * - Crisis services have different rules (per hour or per diem)
 * 
 * SOURCES:
 * - Georgia DBHDD Provider Manual
 * - CMS Medicare Claims Processing Manual, Ch. 5 (8-minute rule)
 * - Georgia Medicaid fee schedules
 */

// ─── Service Billing Configuration ────────────────────────────────
// Each service type defines how billing units are calculated
export const BILLING_RULES = {
  // ===== TIME-BASED (15-min units, 8-minute rule) =====
  "H0039": {
    code: "H0039",
    label: "Assertive Community Treatment / ICM",
    unitType: "time",           // "time" | "encounter" | "diem" | "hourly"
    unitMinutes: 15,            // 1 unit = 15 minutes
    minBillableMinutes: 8,      // Must have at least 8 min to bill 1 unit
    maxUnitsPerDay: 16,         // Cap: 4 hours per day
    rate: 17.12,                // Georgia Medicaid rate per unit (approximate)
    requiresFaceToFace: true,
    requiresCommunityBased: true,
    modifiers: [],              // e.g., ["HN"] for bachelor's level
    services: ["ICM Weekly Visit", "ICM First Meeting", "ICM Follow-up", "ICM Initial Assessment", "ACT Contact"],
  },

  "H2017": {
    code: "H2017",
    label: "Psychosocial Rehabilitation (PSR)",
    unitType: "time",
    unitMinutes: 15,
    minBillableMinutes: 8,
    maxUnitsPerDay: 24,         // PSR can be full-day program (6 hours)
    rate: 14.64,
    requiresFaceToFace: true,
    requiresCommunityBased: false,
    modifiers: [],
    services: ["PSR Session"],
  },

  "H2015": {
    code: "H2015",
    label: "Community Support — Individual",
    unitType: "time",
    unitMinutes: 15,
    minBillableMinutes: 8,
    maxUnitsPerDay: 16,
    rate: 15.88,
    requiresFaceToFace: true,
    requiresCommunityBased: true,
    modifiers: [],
    services: ["CST Visit"],
  },

  "H0038": {
    code: "H0038",
    label: "Peer Support Services",
    unitType: "time",
    unitMinutes: 15,
    minBillableMinutes: 8,
    maxUnitsPerDay: 8,          // 2 hours max typical
    rate: 10.50,
    requiresFaceToFace: true,
    requiresCommunityBased: true,
    modifiers: ["HN"],          // Peer specialist modifier
    services: ["Peer Support"],
  },

  "H2011": {
    code: "H2011",
    label: "Crisis Intervention",
    unitType: "time",
    unitMinutes: 15,
    minBillableMinutes: 8,
    maxUnitsPerDay: 32,         // Crisis can be extended
    rate: 21.75,
    requiresFaceToFace: true,
    requiresCommunityBased: false,
    modifiers: [],
    services: ["Crisis Intervention"],
  },

  // ===== ENCOUNTER-BASED (flat per visit) =====
  "90837": {
    code: "90837",
    label: "Psychotherapy, 53+ minutes",
    unitType: "encounter",
    unitMinutes: null,          // Not time-based units
    minBillableMinutes: 53,     // Must be at least 53 min to use this code
    maxUnitsPerDay: 1,
    rate: 132.00,               // Per encounter
    requiresFaceToFace: true,
    requiresCommunityBased: false,
    modifiers: [],
    services: ["Individual Therapy 60min"],
  },

  "90834": {
    code: "90834",
    label: "Psychotherapy, 38-52 minutes",
    unitType: "encounter",
    unitMinutes: null,
    minBillableMinutes: 38,
    maxUnitsPerDay: 1,
    rate: 104.00,
    requiresFaceToFace: true,
    requiresCommunityBased: false,
    modifiers: [],
    services: ["Individual Therapy 45min"],
  },

  "90832": {
    code: "90832",
    label: "Psychotherapy, 16-37 minutes",
    unitType: "encounter",
    unitMinutes: null,
    minBillableMinutes: 16,
    maxUnitsPerDay: 1,
    rate: 68.00,
    requiresFaceToFace: true,
    requiresCommunityBased: false,
    modifiers: [],
    services: ["Individual Therapy 30min"],
  },

  "99213": {
    code: "99213",
    label: "Medication Management — Established Patient",
    unitType: "encounter",
    unitMinutes: null,
    minBillableMinutes: 10,
    maxUnitsPerDay: 1,
    rate: 92.00,
    requiresFaceToFace: true,
    requiresCommunityBased: false,
    modifiers: [],
    services: ["Medication Management"],
  },
};


// ─── Core Calculation Functions ───────────────────────────────────

/**
 * Calculate billing units from session duration using the 8-minute rule.
 * 
 * @param {number} durationMinutes - Total face-to-face time in minutes
 * @param {string} billingCode - HCPCS/CPT code (e.g., "H0039")
 * @returns {object} { units, code, rate, totalReimbursement, unitType, warnings }
 */
export function calculateBillingUnits(durationMinutes, billingCode) {
  const rule = BILLING_RULES[billingCode];
  
  if (!rule) {
    return {
      units: 0,
      code: billingCode,
      rate: 0,
      totalReimbursement: 0,
      unitType: "unknown",
      warnings: [`Unknown billing code: ${billingCode}`],
    };
  }

  const warnings = [];
  let units = 0;

  if (rule.unitType === "time") {
    // ─── 8-Minute Rule for Time-Based Services ───
    if (durationMinutes < rule.minBillableMinutes) {
      warnings.push(`Session too short to bill. Minimum ${rule.minBillableMinutes} minutes required, got ${durationMinutes} minutes.`);
      units = 0;
    } else {
      // Standard 8-minute rule calculation
      // Full units = floor(duration / unitMinutes)
      // Remaining minutes: if >= 8, add 1 more unit
      const fullUnits = Math.floor(durationMinutes / rule.unitMinutes);
      const remainder = durationMinutes % rule.unitMinutes;
      units = remainder >= 8 ? fullUnits + 1 : fullUnits;
    }

    // Cap at max units per day
    if (units > rule.maxUnitsPerDay) {
      warnings.push(`Units capped at ${rule.maxUnitsPerDay} per day (calculated ${units}).`);
      units = rule.maxUnitsPerDay;
    }

  } else if (rule.unitType === "encounter") {
    // ─── Encounter-Based (flat per visit) ───
    if (durationMinutes < rule.minBillableMinutes) {
      warnings.push(`Session duration (${durationMinutes} min) below minimum for ${rule.code} (requires ${rule.minBillableMinutes} min).`);
      units = 0;
    } else {
      units = 1;
    }

  } else if (rule.unitType === "hourly") {
    units = Math.ceil(durationMinutes / 60);
    if (units > rule.maxUnitsPerDay) {
      units = rule.maxUnitsPerDay;
    }

  } else if (rule.unitType === "diem") {
    units = 1; // Per diem = 1 unit per day
  }

  const totalReimbursement = rule.unitType === "encounter" 
    ? (units > 0 ? rule.rate : 0)
    : units * rule.rate;

  return {
    units,
    code: rule.code,
    label: rule.label,
    rate: rule.rate,
    unitType: rule.unitType,
    totalReimbursement: Math.round(totalReimbursement * 100) / 100,
    modifiers: rule.modifiers,
    durationMinutes,
    warnings,
  };
}


/**
 * Parse a duration string like "45 min" or "1h 23m" into minutes.
 * Handles formats from the app:
 *   "45 min", "1h 23m", "01:23:45", "45", "1.5 hours"
 */
export function parseDurationToMinutes(durationStr) {
  if (!durationStr) return 0;
  
  // If already a number, return it
  if (typeof durationStr === "number") return Math.round(durationStr);

  const str = durationStr.toString().trim().toLowerCase();

  // "HH:MM:SS" or "MM:SS" format
  const colonMatch = str.match(/^(\d+):(\d+)(?::(\d+))?$/);
  if (colonMatch) {
    if (colonMatch[3] !== undefined) {
      // HH:MM:SS
      return parseInt(colonMatch[1]) * 60 + parseInt(colonMatch[2]) + (parseInt(colonMatch[3]) >= 30 ? 1 : 0);
    }
    // MM:SS
    return parseInt(colonMatch[1]) + (parseInt(colonMatch[2]) >= 30 ? 1 : 0);
  }

  // "Xh Ym" format
  const hmMatch = str.match(/(\d+)\s*h(?:ours?|r)?\s*(?:(\d+)\s*m(?:in)?)?/);
  if (hmMatch) {
    return parseInt(hmMatch[1]) * 60 + (parseInt(hmMatch[2]) || 0);
  }

  // "X min" or "X minutes"
  const minMatch = str.match(/(\d+)\s*min/);
  if (minMatch) return parseInt(minMatch[1]);

  // "X hours"
  const hourMatch = str.match(/([\d.]+)\s*hour/);
  if (hourMatch) return Math.round(parseFloat(hourMatch[1]) * 60);

  // Bare number — assume minutes
  const bareNum = parseInt(str);
  if (!isNaN(bareNum)) return bareNum;

  return 0;
}


/**
 * Look up the billing code for a service type name.
 * Maps service type strings → HCPCS/CPT codes.
 */
export function getBillingCodeForService(serviceType) {
  if (!serviceType) return "H0039"; // Default to ICM

  const normalized = serviceType.toLowerCase().trim();

  // Check each rule's services array
  for (const [code, rule] of Object.entries(BILLING_RULES)) {
    for (const svc of rule.services) {
      if (svc.toLowerCase() === normalized) return code;
    }
  }

  // Fuzzy matching
  const fuzzyMap = {
    "icm": "H0039",
    "case management": "H0039",
    "act": "H0039",
    "assertive": "H0039",
    "psr": "H2017",
    "psychosocial": "H2017",
    "skill": "H2017",
    "cst": "H2015",
    "community support": "H2015",
    "peer": "H0038",
    "crisis": "H2011",
    "therapy 60": "90837",
    "therapy 45": "90834",
    "therapy 30": "90832",
    "individual therapy": "90834", // Default to 45min
    "medication": "99213",
    "med management": "99213",
    "psychiatry": "99213",
  };

  for (const [key, code] of Object.entries(fuzzyMap)) {
    if (normalized.includes(key)) return code;
  }

  return "H0039"; // Default
}


/**
 * Full billing calculation from a note object.
 * This is the main function to call when a note is submitted.
 * 
 * @param {object} note - The note object with duration, serviceType, etc.
 * @returns {object} Complete billing info ready to store on the note
 */
export function calculateNoteBilling(note) {
  // Extract duration from note
  const durationMinutes = parseDurationToMinutes(
    note.durationMinutes || note.duration || note.noteData?.header?.duration || 0
  );

  // Get billing code from service type
  const serviceType = note.serviceType || note.noteData?.header?.serviceType || "ICM Weekly Visit";
  const billingCode = getBillingCodeForService(serviceType);

  // Calculate
  const result = calculateBillingUnits(durationMinutes, billingCode);

  return {
    ...result,
    serviceType,
    billingCode,
    calculatedAt: new Date().toISOString(),
    // Flag for review if there are warnings
    needsBillingReview: result.warnings.length > 0,
  };
}


/**
 * 8-minute rule reference table for display purposes.
 * Shows exact minute ranges for each unit count.
 */
export function getEightMinuteRuleTable(billingCode = "H0039") {
  const rule = BILLING_RULES[billingCode];
  if (!rule || rule.unitType !== "time") return [];

  const table = [];
  for (let units = 1; units <= rule.maxUnitsPerDay; units++) {
    const minTime = units === 1 
      ? rule.minBillableMinutes 
      : ((units - 1) * rule.unitMinutes) + 8;
    const maxTime = (units * rule.unitMinutes) + 7;
    
    table.push({
      units,
      minMinutes: minTime,
      maxMinutes: Math.min(maxTime, units * rule.unitMinutes + rule.unitMinutes - 1),
      reimbursement: Math.round(units * rule.rate * 100) / 100,
    });
  }
  return table;
}


/**
 * Validate billing before claim submission.
 * Returns array of issues that need to be resolved.
 */
export function validateBilling(note, individual) {
  const issues = [];
  const billing = note.billingInfo || calculateNoteBilling(note);

  if (billing.units === 0) {
    issues.push({ severity: "error", message: "No billable units — session too short or invalid code." });
  }

  if (billing.warnings?.length > 0) {
    billing.warnings.forEach(w => issues.push({ severity: "warning", message: w }));
  }

  // Check for required fields
  if (!note.serviceType) {
    issues.push({ severity: "error", message: "Missing service type." });
  }

  if (!note.duration && !note.durationMinutes) {
    issues.push({ severity: "error", message: "Missing session duration." });
  }

  // Check individual has required billing info
  if (individual) {
    if (!individual.medicaidId && !individual.insurance?.primaryPayerMemberId) {
      issues.push({ severity: "warning", message: "No Medicaid ID or insurance member ID on file." });
    }
  }

  // Check note is signed
  if (note.status !== "signed") {
    issues.push({ severity: "error", message: "Note must be co-signed before billing." });
  }

  return issues;
}

export default {
  BILLING_RULES,
  calculateBillingUnits,
  calculateNoteBilling,
  parseDurationToMinutes,
  getBillingCodeForService,
  getEightMinuteRuleTable,
  validateBilling,
};